/**
 * DesignLanguageShell — the layout route every Design Language page sits in.
 *
 * Design Language is internal team material that stays with Priorities.ai
 * when Elevation's own pages leave for a separate app (Proposal 09 § 4
 * coupling 6 and § 8.2, ~/Vault/_system/proposals/09-elevation-separation.md).
 * Until now it borrowed Elevation's gate (RequireElevationAccess), its body
 * and theme (AppElevationBody, ElevationThemeEnforcer) and, through the team
 * bar, its chrome. This shell gives it its own, so removing Elevation cannot
 * break it. The addresses are unchanged; the Elevation chrome in App.tsx steps
 * aside on them (designLanguagePaths.ts).
 *
 * The gate is the team level from lib/elevationAccess.ts (Wes and Harry;
 * customers never), plus the two switches the pages already answered to: the
 * `elevation_platform` workspace module and the `elevation_nav` flag. Anyone
 * else goes to /prioritization/dashboard. Nothing is drawn while it is not yet
 * known who is signed in; while the workspace modules are still loading the
 * page is drawn, after the level check, exactly as RequireElevationAccess does.
 *
 * The markup reproduces what the pages sat in before: the daylight body with
 * the .dark class for dark, the team bar pinned at the top, and the container
 * main the pages' own negative margins were written against.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { TeamHomeBar } from '../../components/TeamHomeBar';
import { Button } from '../../components/ui/Button';
import { hasElevationLevel, useElevationAccess } from '../../lib/elevationAccess';
import { FEATURE_FLAGS, useFeatureFlag } from '../../lib/featureFlags';
import { isModuleEnabled, useWorkspaceModules } from '../../lib/workspaceModulesService';
import { type DesignLanguageTheme, useDesignLanguageTheme } from './useDesignLanguageTheme';

const BLOCKED_REDIRECT = '/prioritization/dashboard';

/** The workspace module Design Language has always answered to (Proposal 09 § 4 coupling 10 settles it). */
const DESIGN_LANGUAGE_MODULE = 'elevation_platform';

function ThemeSwitch({ theme, onChange }: { theme: DesignLanguageTheme; onChange: (t: DesignLanguageTheme) => void }) {
  const label = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onChange(theme === 'dark' ? 'light' : 'dark')}
      aria-label={label}
      title={label}
      className="!p-1.5 !text-[var(--p-label)] hover:!text-[var(--p-ink)]"
    >
      {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
    </Button>
  );
}

export function DesignLanguageShell() {
  const [theme, setTheme] = useDesignLanguageTheme();
  const access = useElevationAccess();
  const workspaceFeatures = useWorkspaceModules();
  const flagEnabled = useFeatureFlag(FEATURE_FLAGS.ELEVATION_NAV);

  if (access === 'pending') return null;
  if (!hasElevationLevel(access, 'team')) return <Navigate to={BLOCKED_REDIRECT} replace />;

  // While the workspace modules are still being fetched (empty object), draw
  // the page rather than flash a redirect; the level check above already ran.
  const modulesLoading = Object.keys(workspaceFeatures).length === 0;
  if (!modulesLoading && (!isModuleEnabled(workspaceFeatures, DESIGN_LANGUAGE_MODULE) || !flagEnabled)) {
    return <Navigate to={BLOCKED_REDIRECT} replace />;
  }

  return (
    <div
      data-testid="design-language-shell"
      className={`daylight min-h-screen flex flex-col ${theme === 'dark' ? 'dark bg-[var(--bg)]' : 'bg-[var(--p-paper)]'}`}
    >
      <div className="sticky top-0 z-50">
        <TeamHomeBar showElevation={access === 'owner'} actions={<ThemeSwitch theme={theme} onChange={setTheme} />} />
      </div>
      <main className="container mx-auto px-4 py-8 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
